import React from 'react'

import cn from 'classnames'

import { VideoAssetData } from '../../../../lib/types'
import { VisuallyHidden } from '../../a11y/visually-hidden'
import styles from './video-player.module.scss'

export type RenderVideoSource = (videoSource: VideoAssetData) => React.ReactNode

const defaultRenderSource: RenderVideoSource = ({ url, contentType }) => <source src={url} type={contentType} />

export interface VideoPlayerProps {
  className?: string
  videoSources: VideoAssetData[]
  renderVideoSource?: RenderVideoSource
  width?: number
  height?: number
  controls?: boolean
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  poster?: string
  preload?: string
  playsInline?: boolean
  fallbackText?: React.ReactElement
  videoDescription?: string
  dropShadow?: boolean
  onEnded?: () => void
  onLoadedMetadata?: (data: HTMLVideoElement) => void
}

const defaultFallbackText = "Sorry, your browser doesn't support embedded videos"

export const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      className,
      width,
      height,
      videoSources,
      renderVideoSource = defaultRenderSource,
      poster,
      preload,
      fallbackText = defaultFallbackText,
      videoDescription,
      playsInline,
      muted,
      dropShadow = false,
      onLoadedMetadata,
      // a11y-recommended defaults:
      controls = true,
      autoPlay = false,
      loop = false,
      onEnded,
    },
    ref
  ) => {
    const sources = videoSources.map((videoSource) => (
      <React.Fragment key={`${videoSource.url}-${videoSource.contentType}`}>
        {renderVideoSource(videoSource)}
      </React.Fragment>
    ))

    return (
      <>
        <video
          ref={ref}
          className={cn(styles.video, dropShadow && styles.dropShadow, className)}
          width={width}
          height={height}
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          poster={poster}
          preload={preload}
          onEnded={onEnded}
          onLoadedMetadata={(data) => onLoadedMetadata?.(data.target as HTMLVideoElement)}
        >
          {sources}
          {fallbackText}
        </video>
        {videoDescription && <VisuallyHidden>{videoDescription}</VisuallyHidden>}
      </>
    )
  }
)
